import React, { useState, useMemo, useCallback, useEffect, useRef, memo } from "react";
import XLSX from "xlsx-js-style";
import { applyRegisterStyle, writeFileWithPageSetup, fileDate } from "@/utils/excelStyle";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Search,
  FileText,
  Building2,
  Calendar,
  Euro,
  FileEdit,
  Briefcase,
  X,
  Filter,
  PlayCircle,
  CheckCircle2,
  PauseCircle,
  ChevronDown,
  ChevronUp,
  MessageSquare,
  Plus,
  Send,
  Printer,
  Download,
  FolderOpen
} from "lucide-react";
import { currency, formatDate } from "@/utils/helpers";
import { statusConfig, priorityConfig } from "@/utils/constants";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { usePagination } from "@/lib/usePagination";
import api from "@/api";
import { buildPrintDocument, openPrintWindow } from "@/utils/printLayout";
import ModulePageLayout, { KpiPill } from "@/components/common/ModulePageLayout";

/**
 * Pratiche Tab Component - Compact Invoice-Style Layout
 * Shows cases list with filtering, status badges, and CRUD operations
 * Default ordering: newest first (by openingDate descending)
 */

// ─── PDF Icon (inline SVG, 30/06/2026) ──────────────────────────────
// lucide-react non offre un'icona "PDF" esplicita. Quella standard
// universalmente riconoscibile è un documento rosso con la sigla
// "PDF" bianca. La definiamo inline come piccola SVG così non
// servono nuovi asset.
//
// Misura: 14×14 px (h-3.5 w-3.5) per coerenza con le altre icone
// della colonna Azioni (Printer, FileEdit, MessageSquare). Colori
// hard-coded perché il glifo è iconografico, non thematic.
const PdfIcon = ({ className = "h-3.5 w-3.5" }) => (
  <svg
    viewBox="0 0 24 24"
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    {/* Document body */}
    <path d="M5 2.5h9.5L20 8v13.5H5z" fill="#DC2626" stroke="#7F1D1D" strokeWidth="0.6" strokeLinejoin="round" />
    {/* Folded corner */}
    <path d="M14.5 2.5V8H20" fill="#FCA5A5" stroke="#7F1D1D" strokeWidth="0.6" strokeLinejoin="round" />
    {/* "PDF" label */}
    <text
      x="12.5"
      y="18.3"
      fontSize="6.2"
      fontFamily="Helvetica, Arial, sans-serif"
      fontWeight="900"
      fill="#FFFFFF"
      textAnchor="middle"
      letterSpacing="-0.2"
    >PDF</text>
  </svg>
);

// ─── Wave 3.1 (30/06/2026) — Sub-componenti memoizzati per i bottoni
// stampa nella colonna Azioni. Sostituiscono le IIFE inline che
// venivano eseguite 2× per riga × N righe ad ogni render. Riceve
// solo flag booleani + caseId + callback stabili.
const PrintMandatoButton = memo(function PrintMandatoButton({ caseItem, allowed, isPrinting, onPrint }) {
  const handleClick = useCallback((e) => {
    e.stopPropagation();
    onPrint(caseItem);
  }, [onPrint, caseItem]);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0"
            disabled={!allowed || isPrinting}
            onClick={handleClick}
            data-testid={`print-mandato-${caseItem.id}`}
            aria-label="Stampa Mandato"
          >
            <Printer className={cn("h-3.5 w-3.5", isPrinting && "animate-pulse")} />
          </Button>
        </span>
      </TooltipTrigger>
      <TooltipContent side="top">
        <p>
          {allowed
            ? (isPrinting ? "Generazione Mandato in corso…" : "Stampa Mandato")
            : "Stampa Mandato — disponibile solo per Amministratore, Responsabile o operatori della stessa area"}
        </p>
      </TooltipContent>
    </Tooltip>
  );
});

const PrintDossierButton = memo(function PrintDossierButton({ caseItem, allowed, isPrinting, onPrint }) {
  const handleClick = useCallback((e) => {
    e.stopPropagation();
    onPrint(caseItem);
  }, [onPrint, caseItem]);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0 hover:bg-red-50"
            disabled={!allowed || isPrinting}
            onClick={handleClick}
            data-testid={`print-practice-${caseItem.id}`}
            aria-label="PDF Pratica"
          >
            <PdfIcon className={cn("h-3.5 w-3.5", isPrinting && "animate-pulse")} />
          </Button>
        </span>
      </TooltipTrigger>
      <TooltipContent side="top">
        <p>
          {allowed
            ? (isPrinting ? "Generazione PDF Pratica in corso…" : "PDF Pratica — fascicolo completo (anagrafica, dati, operatori, compensi, scadenze, documenti, note, chiusura, cronologia)")
            : "PDF Pratica — disponibile solo per Amministratore, Responsabile o operatori della stessa area"}
        </p>
      </TooltipContent>
    </Tooltip>
  );
});

// ─── Wave 3.2 (30/06/2026) — PracticeRow memoizzata.
// Una singola riga della tabella Registro Pratiche + le sue
// eventuali expanded panel (dettaglio) e notes panel. Avvolta in
// React.memo così la riga si ridisegna SOLO quando una sua prop
// cambia (es. caseItem aggiornato, isExpanded toggle, isPrintingX
// flag). Pre-fix: ogni keystroke nella ricerca o ogni click su
// "espandi" causava una riconciliazione di TUTTE e 41 le righe →
// long task 446ms. Post-fix: solo la riga toccata ridisegna.
//
// Tutti i callback sono stabili dal parent (latest-ref pattern).
// `client` arriva già risolto via `clientsById` Map (O(1) invece
// di find O(n)).
const PracticeRow = memo(function PracticeRow({
  caseItem,
  client,
  isExpanded,
  isShowingNotes,
  isPrintingMandato,
  isPrintingDossier,
  isSavingNote,
  quickNoteValue,
  allowPrint,
  currentOperator,
  onToggleExpand,
  onToggleNotes,
  onEdit,
  onStatusChange,
  onPrintMandato,
  onPrintDossier,
  onQuickNoteSubmit,
  onQuickNoteChange,
  getOperatorName,
  getDisplayNotes,
}) {
  const handleRowClick = useCallback(() => onToggleExpand(caseItem.id), [onToggleExpand, caseItem.id]);
  const handleNotesClick = useCallback((e) => { e.stopPropagation(); onToggleNotes(caseItem.id); }, [onToggleNotes, caseItem.id]);
  const handleEditClick = useCallback((e) => { e.stopPropagation(); onEdit(caseItem); }, [onEdit, caseItem]);
  // 01/07/2026 — handler stabile per Stampa Pratica dal pannello di
  // dettaglio (spostato qui dalla toolbar della riga).
  const handlePrintDossierClick = useCallback((e) => { e.stopPropagation(); onPrintDossier(caseItem); }, [onPrintDossier, caseItem]);
  const handleStatusChange = useCallback((value) => onStatusChange(caseItem.id, value), [onStatusChange, caseItem.id]);
  const handleQuickNoteInput = useCallback((e) => onQuickNoteChange(caseItem.id, e.target.value), [onQuickNoteChange, caseItem.id]);
  const handleQuickNoteKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onQuickNoteSubmit(caseItem);
    }
  }, [onQuickNoteSubmit, caseItem]);
  const handleQuickNoteSave = useCallback(() => onQuickNoteSubmit(caseItem), [onQuickNoteSubmit, caseItem]);

  // Unified diritti+bolli (logica preservata dal codice originale)
  const dirittiBolli = isExpanded
    ? (Number(caseItem.importoDirittiBolli)
        || ((Number(caseItem.importoDiritti) || 0) + (Number(caseItem.importoBolli) || 0)))
    : 0;

  return (
    <React.Fragment>
      <TableRow
        className={cn("cursor-pointer transition-colors", isExpanded && "bg-slate-50")}
        onClick={handleRowClick}
      >
        <TableCell className="py-2">
          <div className="flex flex-col">
            <span className="font-mono text-xs font-medium text-slate-700">
              {caseItem.caseNumber}
            </span>
            <Badge
              variant="outline"
              className={cn(
                "text-[9px] px-1 py-0 w-fit mt-0.5",
                priorityConfig[caseItem.priority]?.color
              )}
            >
              {caseItem.priority}
            </Badge>
          </div>
        </TableCell>
        <TableCell className="py-2">
          <div className="flex flex-col min-w-0">
            <div className="flex items-center gap-1.5">
              <Building2 className="h-3 w-3 text-slate-400 shrink-0" />
              <span className="text-sm font-medium truncate">
                {client?.fullName || "—"}
              </span>
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <p className="text-xs text-slate-500 truncate max-w-[250px] cursor-help">
                  {caseItem.subject}
                </p>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-sm">
                <p>{caseItem.subject}</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </TableCell>
        <TableCell className="py-2 hidden md:table-cell">
          <div className="flex items-center gap-1 text-xs text-slate-600">
            <Calendar className="h-3 w-3 text-slate-400" />
            {formatDate(caseItem.openingDate)}
          </div>
        </TableCell>
        <TableCell className="py-2 hidden lg:table-cell">
          {caseItem.estimatedFee > 0 ? (
            <span className="text-sm font-medium text-green-600">
              {currency(caseItem.estimatedFee)}
            </span>
          ) : (
            <span className="text-xs text-slate-400">—</span>
          )}
        </TableCell>
        <TableCell className="py-2">
          <Badge className={cn("text-[10px] px-1.5 py-0", statusConfig[caseItem.status]?.color)}>
            {caseItem.status}
          </Badge>
        </TableCell>
        <TableCell className="py-2 text-center">
          <div className="flex items-center justify-center gap-1">
            {/* 1. Note/Appunti button */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  className={cn("h-7 w-7 p-0", isShowingNotes && "bg-blue-100")}
                  onClick={handleNotesClick}
                  data-testid={`notes-case-${caseItem.id}`}
                >
                  <MessageSquare className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p>Note Interne</p>
              </TooltipContent>
            </Tooltip>

            {/* 2. Stato - quick status selector */}
            <Select value={caseItem.status} onValueChange={handleStatusChange}>
              <SelectTrigger className="h-7 w-[90px] text-[10px] px-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.keys(statusConfig).map((status) => (
                  <SelectItem key={status} value={status} className="text-xs">
                    {status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* 3. Modifica button */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 w-7 p-0"
                  onClick={handleEditClick}
                  data-testid={`edit-case-${caseItem.id}`}
                >
                  <FileEdit className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p>Modifica</p>
              </TooltipContent>
            </Tooltip>

            {/* 4. Stampa Mandato — icona compatta nella toolbar.
                01/07/2026 — Rimosso PrintDossierButton dalla toolbar
                (creava confusione con Mandato); ora la stampa del
                Fascicolo Pratica è disponibile nel pannello di
                dettaglio della riga espansa. Vedi sotto. */}
            <PrintMandatoButton
              caseItem={caseItem}
              allowed={allowPrint}
              isPrinting={isPrintingMandato}
              onPrint={onPrintMandato}
            />

            {/* Expand/collapse chevron */}
            {isExpanded ? (
              <ChevronUp className="h-4 w-4 text-slate-400" />
            ) : (
              <ChevronDown className="h-4 w-4 text-slate-400" />
            )}
          </div>
        </TableCell>
      </TableRow>

      {/* Expanded Row Details */}
      {isExpanded && (
        <TableRow className="bg-slate-50 hover:bg-slate-50">
          <TableCell colSpan={6} className="py-3 px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
              <div>
                <span className="text-slate-500">Tipologia:</span>
                <p className="font-medium">{caseItem.practiceType}</p>
              </div>
              <div>
                <span className="text-slate-500">Ente:</span>
                {caseItem.enteCoinvolto && caseItem.enteCoinvolto.includes(',') ? (
                  <div className="flex flex-col gap-0.5 mt-0.5">
                    {caseItem.enteCoinvolto.split(',').map((ente, i) => (
                      <p key={i} className="font-medium text-xs">{ente.trim()}</p>
                    ))}
                  </div>
                ) : (
                  <p className="font-medium">{caseItem.enteCoinvolto || "—"}</p>
                )}
              </div>
              {caseItem.practiceArea && (
                <div>
                  <span className="text-slate-500">Tipo Pratica:</span>
                  <p className="font-medium">{caseItem.practiceArea}</p>
                </div>
              )}
              <div>
                <span className="text-slate-500">Scadenza:</span>
                <p className="font-medium">{caseItem.dueDate ? formatDate(caseItem.dueDate) : "—"}</p>
              </div>
              {dirittiBolli > 0 && (
                <div>
                  <span className="text-slate-500">Di cui diritti e bolli:</span>
                  <p className="font-medium">{currency(dirittiBolli)}</p>
                </div>
              )}
              <div>
                <span className="text-slate-500">Canale:</span>
                <p className="font-medium">{caseItem.requestChannel || "—"}</p>
              </div>
              <div>
                <span className="text-slate-500">Operatore:</span>
                <p className="font-medium">
                  {caseItem.assignedOperatorId ? getOperatorName(caseItem.assignedOperatorId) : "—"}
                </p>
              </div>
              {caseItem.description && (
                <div className="col-span-2">
                  <span className="text-slate-500">Note:</span>
                  <p className="font-medium">{caseItem.description}</p>
                </div>
              )}
              {caseItem.closedAt && (
                <div className="col-span-2 md:col-span-4 pt-2 mt-2 border-t border-emerald-200/50">
                  <span className="text-emerald-700 text-xs font-semibold uppercase">Chiusura pratica:</span>
                  <p className="font-medium text-emerald-900">
                    {formatDate(caseItem.closedAt)}
                    {caseItem.closedAt.includes("T") && ` alle ${caseItem.closedAt.slice(11, 16)}`}
                    {caseItem.closedByOperatorName && ` · da ${caseItem.closedByOperatorName}`}
                  </p>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-3 pt-3 border-t">
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                onClick={handleEditClick}
                data-testid={`expanded-edit-${caseItem.id}`}
              >
                <FileEdit className="h-3 w-3 mr-1" />
                Modifica
              </Button>
              {/* 01/07/2026 — Spostato qui dalla toolbar della riga per
                  evitare confusione con Stampa Mandato. Il PDF Fascicolo
                  Pratica è un'operazione meno frequente e più
                  "consapevole": l'utente entra prima nel dettaglio,
                  poi genera il fascicolo. */}
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs hover:bg-red-50"
                onClick={handlePrintDossierClick}
                disabled={!allowPrint || isPrintingDossier}
                data-testid={`print-practice-${caseItem.id}`}
                title={
                  allowPrint
                    ? (isPrintingDossier ? "Generazione PDF in corso…" : "Genera PDF fascicolo completo della pratica (anagrafica, dati, operatori, compensi, scadenze, documenti, note, chiusura, cronologia)")
                    : "PDF Pratica — disponibile solo per Amministratore, Responsabile o operatori della stessa area"
                }
              >
                <PdfIcon className={cn("h-3 w-3 mr-1", isPrintingDossier && "animate-pulse")} />
                {isPrintingDossier ? "Generazione…" : "Stampa Pratica"}
              </Button>
            </div>
          </TableCell>
        </TableRow>
      )}

      {/* Notes Row */}
      {isShowingNotes && (
        <TableRow className="bg-blue-50 hover:bg-blue-50">
          <TableCell colSpan={6} className="py-2 px-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-3.5 w-3.5 text-blue-600" />
                <span className="text-xs font-medium text-blue-800">Note Interne</span>
              </div>
            </div>

            {/* Existing notes */}
            {(() => {
              const notes = getDisplayNotes(caseItem);
              if (notes.length > 0) {
                return (
                  <div className="space-y-0.5 max-h-[200px] overflow-y-auto mb-2">
                    {notes.map((note, idx) => {
                      const noteDate = new Date(note.timestamp);
                      const dateStr = noteDate.toLocaleDateString('it-IT');
                      const timeStr = noteDate.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
                      return (
                        <div key={idx} className="text-xs flex items-baseline gap-2 py-0.5 border-b border-blue-100 last:border-0">
                          <span className="text-slate-400 whitespace-nowrap">{dateStr}</span>
                          <span className="text-slate-400 whitespace-nowrap">{timeStr}</span>
                          <span className="font-medium text-blue-600 whitespace-nowrap">{note.operatorName}</span>
                          <span className="text-slate-700">{note.text}</span>
                        </div>
                      );
                    })}
                  </div>
                );
              }
              return null;
            })()}

            {/* Quick add note input */}
            <div className="flex items-center gap-2 pt-1 border-t border-blue-100">
              <Input
                placeholder="Aggiungi nota rapida (telefono, WhatsApp, email, incontro)..."
                value={quickNoteValue}
                onChange={handleQuickNoteInput}
                className="flex-1 h-7 text-xs"
                onKeyDown={handleQuickNoteKeyDown}
              />
              <Button
                size="sm"
                variant="default"
                className="h-7 px-2"
                disabled={!quickNoteValue?.trim() || isSavingNote}
                onClick={handleQuickNoteSave}
              >
                {isSavingNote ? (
                  <span className="text-xs">...</span>
                ) : (
                  <>
                    <Send className="h-3 w-3 mr-1" />
                    <span className="text-xs">Salva</span>
                  </>
                )}
              </Button>
            </div>
            {currentOperator && (
              <p className="text-[10px] text-slate-400 mt-1">
                Firmata da: {currentOperator.fullName || currentOperator.name}
              </p>
            )}
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  );
});

function PraticheTab({
  cases,
  setCases,
  clients,
  deadlines,
  operators,
  filteredCases,
  searchQuery,
  setSearchQuery,
  filterStatus,
  setFilterStatus,
  filterPriority,
  setFilterPriority,
  filterPracticeType,
  setFilterPracticeType,
  filterManagementType,
  setFilterManagementType,
  handleEditCase,
  handleDeleteCase,
  handleQuickStatusChange,
  getOperatorName,
  onCreateInvoiceFromCase,
  currentOperator,
  // Nuova Pratica button (29/06/2026) — callback wired in App.js
  // a `initNewCaseForm()` + `setIsCaseDialogOpen(true)`. Stesso flow
  // del Cruscotto: nessuna differenza di comportamento. Il refresh
  // post-save è automatico (lo stato `cases` in App.js viene
  // aggiornato dal CaseDialog, PraticheTab riceve la nuova prop e
  // ri-renderizza la lista filtrata).
  onNewCase,
}) {
  // Card filter state for summary cards
  const [cardFilter, setCardFilter] = useState(null);
  // Year filter
  const [filterYear, setFilterYear] = useState("all");
  // Expanded row state
  const [expandedRow, setExpandedRow] = useState(null);
  // Show notes state (separate from expanded)
  const [showNotesForCase, setShowNotesForCase] = useState(null);
  // Quick note input state per case
  const [quickNoteInput, setQuickNoteInput] = useState({});
  // Saving state
  const [savingNoteFor, setSavingNoteFor] = useState(null);

  // Handle quick note submission
  const handleQuickNoteSubmit = useCallback(async (caseItem) => {
    const noteText = quickNoteInput[caseItem.id];
    if (!noteText?.trim()) return;
    
    setSavingNoteFor(caseItem.id);
    
    const operatorName = currentOperator?.fullName || currentOperator?.name || "Utente";
    const newNote = {
      text: noteText.trim(),
      operatorId: currentOperator?.id || null,
      operatorName: operatorName,
      timestamp: new Date().toISOString()
    };
    
    const existingNotes = caseItem.structuredNotes || [];
    const updatedNotes = [...existingNotes, newNote];
    
    // Update legacy format too
    const legacyFormat = updatedNotes.map(n => 
      `[${new Date(n.timestamp).toLocaleString('it-IT')} - ${n.operatorName}] ${n.text}`
    ).join('\n');
    
    try {
      // Update case via API
      const updatedCase = {
        ...caseItem,
        structuredNotes: updatedNotes,
        internalNotes: legacyFormat
      };
      delete updatedCase.id;
      delete updatedCase.createdAt;
      delete updatedCase.updatedAt;
      
      await api.updateCase(caseItem.id, updatedCase);
      
      // Update local state
      setCases(prev => prev.map(c => 
        c.id === caseItem.id 
          ? { ...c, structuredNotes: updatedNotes, internalNotes: legacyFormat }
          : c
      ));
      
      // Clear input
      setQuickNoteInput(prev => ({ ...prev, [caseItem.id]: "" }));
    } catch (error) {
      console.error("Failed to save note:", error);
      alert("Errore nel salvare la nota");
    } finally {
      setSavingNoteFor(null);
    }
  }, [quickNoteInput, currentOperator, setCases]);

  // Per-row scope check that mirrors the backend gate
  // (`_assert_print_scope` in server.py, 20/02/2026). We disable the
  // quick-print button in the UI when the logged-in operator is
  // outside the case's operational area (operatore_studio vs Agenzia
  // case, and vice-versa), so the click never surfaces a 403 toast.
  // Admin/responsabile always pass.
  const operatorRole = currentOperator?.role || "";
  const isAdminOrResp = operatorRole === "amministratore" || operatorRole === "responsabile";
  const canPrintMandato = useCallback((caseItem) => {
    if (isAdminOrResp) return true;
    const ptype = (caseItem?.practiceType || "").toLowerCase();
    const isAgenzia = ptype.includes("agenzia");
    if (operatorRole === "operatore_studio")  return !isAgenzia;
    if (operatorRole === "operatore_agenzia") return isAgenzia;
    return true; // unknown role → optimistic, backend will gate.
  }, [isAdminOrResp, operatorRole]);

  // Quick-print: Mandato Pratica (esistente)
  const [printingCaseId, setPrintingCaseId] = useState(null);
  const handleQuickPrintMandato = useCallback(async (caseItem) => {
    if (!caseItem?.id) return;
    setPrintingCaseId(caseItem.id);
    try {
      await api.downloadMandatoPDF(caseItem.id);
      toast.success("Mandato pratica generato");
    } catch (err) {
      const msg = err?.message || "Errore nella stampa del mandato";
      // Friendly mapping for the 403 raised by `_assert_print_scope`.
      if (/permesso negato/i.test(msg) || /403/.test(msg)) {
        toast.error("Stampa non consentita per il tuo ruolo (scope Studio/Agenzia).");
      } else {
        toast.error(msg);
      }
    } finally {
      setPrintingCaseId(null);
    }
  }, []);

  // Quick-print: Fascicolo Pratica completo (28/06/2026).
  // Snapshot amministrativo: anagrafica + dati + date + operatori +
  // compensi + documenti collegati + scadenze + note + chiusura +
  // cronologia completa. Vedi /api/cases/{id}/practice-pdf.
  const [printingDossierId, setPrintingDossierId] = useState(null);
  const handleQuickPrintDossier = useCallback(async (caseItem) => {
    if (!caseItem?.id) return;
    setPrintingDossierId(caseItem.id);
    try {
      await api.downloadPracticePDF(caseItem.id);
      toast.success("Fascicolo pratica generato");
    } catch (err) {
      const msg = err?.message || "Errore nella stampa del fascicolo";
      if (/permesso negato/i.test(msg) || /403/.test(msg)) {
        toast.error("Stampa non consentita per il tuo ruolo (scope Studio/Agenzia).");
      } else {
        toast.error(msg);
      }
    } finally {
      setPrintingDossierId(null);
    }
  }, []);

  // ─── Wave 3.1 (30/06/2026) — clientsById Map per lookup O(1).
  // Pre-fix: `clients.find()` per ogni riga ad ogni render = O(n)
  // per N clients × M righe per render. Post-fix: O(1) lookup.
  const clientsById = useMemo(() => {
    const m = new Map();
    for (const c of clients) m.set(c.id, c);
    return m;
  }, [clients]);

  // ─── Wave 3.2 (30/06/2026) — Aggregated single-pass scan di cases.
  // Pre-fix: 5× useMemo separati che scannavano `cases` ad ogni
  // cambio (stats, availableYears, agenziaArchiveYears, gaCountsByYear,
  // studioArchiveYears, gsCountsByYear) = 6 full scan dell'array.
  // Post-fix: single-pass calcola tutto in una sola scansione.
  const aggregated = useMemo(() => {
    let inLavorazione = 0, nuoveRichieste = 0, completate = 0, sospese = 0;
    const allYears = new Set();
    const gaYears = new Set();
    const gsYears = new Set();
    const gaCounts = {};
    const gsCounts = {};
    for (const c of cases) {
      // status counters
      if (c.status === "In lavorazione") inLavorazione++;
      else if (c.status === "Nuova richiesta") nuoveRichieste++;
      else if (c.status === "Conclusa") completate++;
      else if (c.status === "Sospesa") sospese++;
      // years extraction
      const od = c.openingDate || c.createdAt || "";
      if (od.length >= 4) {
        const yr = od.slice(0, 4);
        allYears.add(yr);
        if (c.practiceType === "Gestionale Agenzia") {
          gaYears.add(yr);
          gaCounts[yr] = (gaCounts[yr] || 0) + 1;
        } else if (c.practiceType === "Gestionale Studio") {
          gsYears.add(yr);
          gsCounts[yr] = (gsCounts[yr] || 0) + 1;
        }
      }
    }
    const sortDesc = (s) => Array.from(s).sort().reverse();
    return {
      stats: { inLavorazione, nuoveRichieste, completate, sospese, totale: cases.length },
      availableYears: sortDesc(allYears),
      agenziaArchiveYears: sortDesc(gaYears),
      studioArchiveYears: sortDesc(gsYears),
      gaCountsByYear: gaCounts,
      gsCountsByYear: gsCounts,
    };
  }, [cases]);
  const { stats, availableYears, agenziaArchiveYears, studioArchiveYears, gaCountsByYear, gsCountsByYear } = aggregated;

  // Sort cases by protocol number (highest/newest first) - DEFAULT ORDERING
  const sortedFilteredCases = useMemo(() => {
    let list = [...filteredCases];
    
    // Apply year filter — BUT skip it when search is active (global cross-year search)
    if (filterYear !== "all" && !searchQuery.trim()) {
      const yr = filterYear;
      list = list.filter(c => {
        const od = c.openingDate || c.createdAt || "";
        return od.startsWith(yr);
      });
    }
    
    return list.sort((a, b) => {
      // Extract numeric part from caseNumber (e.g., "GA-0035" -> 35)
      const getNumericPart = (caseNum) => {
        if (!caseNum) return 0;
        const match = caseNum.match(/\d+/);
        return match ? parseInt(match[0], 10) : 0;
      };
      const numA = getNumericPart(a.caseNumber);
      const numB = getNumericPart(b.caseNumber);
      // Sort by number descending (highest first)
      // If same number, sort by opening date descending
      if (numB !== numA) {
        return numB - numA;
      }
      const dateA = new Date(a.openingDate || a.createdAt || 0);
      const dateB = new Date(b.openingDate || b.createdAt || 0);
      return dateB - dateA;
    });
  }, [filteredCases, filterYear, searchQuery]);

  // Paginazione condivisa — default 50 righe per pagina, persiste
  // la preferenza dell'operatore in localStorage. Spec utente
  // 20/02/2026.
  const {
    paged: pagedCases,
    paginationBar: casesPaginationBar,
  } = usePagination(sortedFilteredCases, {
    storageKey: "registro-pratiche",
    itemLabelSingular: "pratica",
    itemLabelPlural: "pratiche",
  });

  // Available years for filter — già calcolato in `aggregated` (single-pass).

  // Agenzia archive mode: null = show all, "2026" = show only that year
  const [agenziaArchiveYear, setAgenziaArchiveYear] = useState(null);

  // Is showing Agenzia view?
  const isAgenziaView = filterPracticeType === "Gestionale Agenzia";

  // Handle archive year click
  const handleArchiveYearClick = useCallback((year) => {
    if (agenziaArchiveYear === year) {
      setAgenziaArchiveYear(null);
      setFilterYear("all");
    } else {
      setAgenziaArchiveYear(year);
      setFilterYear(year);
      // Auto-set type to Agenzia if not already
      if (filterPracticeType !== "Gestionale Agenzia") {
        setFilterPracticeType("Gestionale Agenzia");
      }
    }
  }, [agenziaArchiveYear, filterPracticeType, setFilterPracticeType]);

  // Count GA cases per year — già calcolato in `aggregated` (single-pass).

  // Studio archive years (GS cases) — già calcolato in `aggregated` (single-pass).

  const [studioArchiveYear, setStudioArchiveYear] = useState(null);

  const handleStudioArchiveYearClick = useCallback((year) => {
    if (studioArchiveYear === year) {
      setStudioArchiveYear(null);
      setFilterYear("all");
    } else {
      setStudioArchiveYear(year);
      setFilterYear(year);
      if (filterPracticeType !== "Gestionale Studio") {
        setFilterPracticeType("Gestionale Studio");
      }
    }
  }, [studioArchiveYear, filterPracticeType, setFilterPracticeType]);

  // gsCountsByYear già calcolato in `aggregated` (single-pass).

  // Handle card click to set filter
  const handleCardClick = useCallback((status) => {
    if (cardFilter === status) {
      setCardFilter(null);
      setFilterStatus("all");
    } else {
      setCardFilter(status);
      setFilterStatus(status);
    }
  }, [cardFilter, setFilterStatus]);

  // Check if any filter is active
  const hasActiveFilters = searchQuery || filterStatus !== "all" || filterPriority !== "all" || filterPracticeType !== "all" || filterManagementType !== "all" || filterYear !== "all" || agenziaArchiveYear;

  // ─── Wave 3.2 (30/06/2026) — Debounced search.
  // Pre-fix: ogni keystroke chiamava setSearchQuery immediatamente
  // → App.js ricalcolava filteredCases → ricalcolo sortedFilteredCases
  // + re-render tabella ad ogni char (4 long task / 12 char misurati).
  // Post-fix: stato locale `searchInput` controlla l'<Input>; un
  // useEffect propaga `setSearchQuery` upstream con delay 200ms.
  // UX invariata (l'utente vede il testo aggiornato immediatamente
  // perché è state locale). Il filter cascade scatta a fine typing.
  const [searchInput, setSearchInput] = useState(searchQuery || "");
  // Sync inverso: se searchQuery viene resettata dall'esterno
  // (resetFilters / unmount tab) → aggiorna l'Input.
  useEffect(() => {
    if (searchQuery !== searchInput) {
      // Sync solo quando la divergenza arriva dall'esterno: confronto
      // a parità di stringa è sufficiente (evita loop).
      // L'utente che digita aggiorna searchInput; il debounce poi
      // riporta searchQuery === searchInput.
      const externalReset = searchQuery === "" && searchInput !== "";
      if (externalReset) setSearchInput("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);
  useEffect(() => {
    if (searchInput === searchQuery) return;
    const t = setTimeout(() => setSearchQuery(searchInput), 200);
    return () => clearTimeout(t);
  }, [searchInput, searchQuery, setSearchQuery]);
  const handleSearchInputChange = useCallback((e) => setSearchInput(e.target.value), []);

  // Reset all filters
  const resetFilters = useCallback(() => {
    setSearchInput("");
    setSearchQuery("");
    setFilterStatus("all");
    setFilterPriority("all");
    setFilterPracticeType("all");
    setFilterManagementType("all");
    setFilterYear("all");
    setCardFilter(null);
    setAgenziaArchiveYear(null);
  }, [setSearchQuery, setFilterStatus, setFilterPriority, setFilterPracticeType, setFilterManagementType]);

  // Toggle row expansion
  const toggleRowExpansion = useCallback((id) => {
    setExpandedRow((cur) => (cur === id ? null : id));
  }, []);

  // Toggle notes visibility for a case (riceve solo l'id; lo
  // stopPropagation è gestito dentro PracticeRow per non rompere il
  // bubbling del row-click).
  const toggleNotes = useCallback((id) => {
    setShowNotesForCase((cur) => (cur === id ? null : id));
  }, []);

  // Parse structured notes or legacy notes
  const getDisplayNotes = useCallback((caseItem) => {
    if (caseItem.structuredNotes && caseItem.structuredNotes.length > 0) {
      return caseItem.structuredNotes;
    }
    if (caseItem.internalNotes) {
      // Split by newlines to show each audit entry as a separate note
      const lines = caseItem.internalNotes.split('\n').filter(l => l.trim());
      return lines.map(line => {
        // Try to parse audit format: [DD/MM/YYYY HH:MM] – Operator – Action
        const match = line.match(/^\[(\d{2}\/\d{2}\/\d{4})\s+(\d{2}:\d{2})\]\s*[–-]\s*(.+?)\s*[–-]\s*(.+)$/);
        if (match) {
          const [, dateStr, timeStr, operator, action] = match;
          const [day, month, year] = dateStr.split('/');
          return {
            text: action.trim(),
            operatorName: operator.trim(),
            timestamp: `${year}-${month}-${day}T${timeStr}:00`
          };
        }
        return {
          text: line.trim(),
          operatorName: "Sistema",
          timestamp: caseItem.createdAt || new Date().toISOString()
        };
      });
    }
    return [];
  }, []);

  // Wave 3.2 — quickNoteInput change handler stabile per riga.
  const handleQuickNoteChange = useCallback((caseId, value) => {
    setQuickNoteInput(prev => ({ ...prev, [caseId]: value }));
  }, []);

  // Print filtered practices with their notes
  const handlePrintPractices = useCallback(() => {
    const filterInfo = [];
    if (cardFilter) filterInfo.push(`Stato: ${cardFilter}`);
    if (filterStatus !== "all") filterInfo.push(`Stato: ${filterStatus}`);
    if (filterPriority !== "all") filterInfo.push(`Priorità: ${filterPriority}`);
    if (filterPracticeType && filterPracticeType !== "all") filterInfo.push(`Tipo: ${filterPracticeType}`);
    if (searchQuery) filterInfo.push(`Ricerca: "${searchQuery}"`);

    const esc = (s) =>
      String(s ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");

    const practicesHtml = sortedFilteredCases.map(caseItem => {
      const client = clients.find(c => c.id === caseItem.clientId);
      const assignedOperator = operators.find(op => op.id === caseItem.assignedOperatorId);
      const operatorName = assignedOperator?.fullName || assignedOperator?.name || getOperatorName(caseItem.assignedOperatorId) || '-';
      const compenso = caseItem.estimatedFee ? currency(caseItem.estimatedFee) : (caseItem.fee ? currency(caseItem.fee) : '-');

      const notes = getDisplayNotes(caseItem);
      const notesHtml = notes.length > 0
        ? notes.map(n => {
            const date = new Date(n.timestamp);
            return `<div style="font-size:9px;color:#666;margin-left:14px;border-left:2px solid var(--gis-green-100,#c7ebd8);padding-left:6px;margin-top:3px;">
              ${date.toLocaleDateString('it-IT')} ${date.toLocaleTimeString('it-IT', {hour:'2-digit',minute:'2-digit'})} —
              <strong>${esc(n.operatorName)}</strong>: ${esc(n.text)}
            </div>`;
          }).join('')
        : '';

      return `
        <tr>
          <td style="font-family:monospace;">${esc(caseItem.caseNumber)}</td>
          <td>
            <strong>${esc(client?.fullName || 'N/D')}</strong>
            ${caseItem.subject ? `<br/><span style="font-size:9.5px;color:#666;">${esc(caseItem.subject)}</span>` : ''}
            ${notesHtml}
          </td>
          <td>${esc(formatDate(caseItem.openingDate))}</td>
          <td style="text-align:right;">${esc(compenso)}</td>
          <td>${esc(operatorName)}</td>
          <td>${esc(caseItem.status)}</td>
        </tr>
      `;
    }).join('');

    const bodyHtml = `
      <p style="font-size:9.5pt;color:#475569;margin:0 0 8px;">
        ${sortedFilteredCases.length} pratiche
        ${filterInfo.length > 0 ? '<br/>Filtri: ' + esc(filterInfo.join(' | ')) : ''}
      </p>
      <table>
        <thead>
          <tr>
            <th style="width:12%;">N. Pratica</th>
            <th>Cliente / Oggetto / Note</th>
            <th style="width:10%;">Data</th>
            <th style="width:11%;">Compenso</th>
            <th style="width:14%;">Operatore</th>
            <th style="width:11%;">Stato</th>
          </tr>
        </thead>
        <tbody>${practicesHtml || '<tr><td colspan="6">Nessuna pratica.</td></tr>'}</tbody>
      </table>`;

    const html = buildPrintDocument({
      title: "Elenco Pratiche",
      subtitle: filterInfo.length > 0 ? `Filtri applicati: ${filterInfo.join(' · ')}` : "Elenco completo",
      bodyHtml,
      orientation: "landscape",
      studioName: "Studio Bonaiuti STP",
    });
    if (!openPrintWindow(html)) {
      toast.error("Popup bloccato dal browser — abilita i popup per stampare.");
    }
  }, [sortedFilteredCases, clients, operators, getOperatorName, cardFilter, filterStatus, filterPriority, filterPracticeType, searchQuery]);

  // Export to Excel using XLSX library
  // Export to Excel - Professional printable register (landscape, no Ora column)
  const handleExportExcel = useCallback(() => {
    const exportDateStr = new Date().toLocaleDateString('it-IT');
    
    let gestioneLabel = "Tutte le Gestioni";
    if (filterPracticeType && filterPracticeType !== "all") {
      gestioneLabel = filterPracticeType === "studio" ? "Gestione Studio" : "Gestione Agenzia";
    }
    
    const numCols = 10;
    const dataHeaders = ["N. Pratica", "Cliente", "Ente", "Oggetto", "Data Apertura", "Scadenza", "Compenso", "Operatore", "Stato", "Priorita"];
    
    const fmt2dec = (val) => Math.round((Number(val) || 0) * 100) / 100;
    const parseDate = (isoString) => {
      if (!isoString) return '';
      try {
        const dt = new Date(isoString);
        if (isNaN(dt.getTime())) {
          if (isoString.includes('T')) return isoString.split('T')[0];
          return isoString;
        }
        return dt.toLocaleDateString('it-IT');
      } catch { return isoString; }
    };
    const formatScadenza = (dateStr) => {
      if (!dateStr) return '';
      try {
        let d = dateStr;
        if (d.includes('T')) d = d.split('T')[0];
        const parts = d.split('-');
        if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
        return d;
      } catch { return dateStr; }
    };
    
    const dataRows = sortedFilteredCases.map(caseItem => {
      const client = clients.find(c => c.id === caseItem.clientId);
      const assignedOperator = operators.find(op => op.id === caseItem.assignedOperatorId);
      const operatorName = assignedOperator?.fullName || assignedOperator?.name || getOperatorName(caseItem.assignedOperatorId) || '';
      const compenso = fmt2dec(caseItem.estimatedFee || caseItem.fee || 0);
      const openingDate = parseDate(caseItem.openingDate || caseItem.createdAt);
      const scadenza = formatScadenza(caseItem.dueDate || caseItem.deadline || '');
      const ente = caseItem.enteCoinvolto || '';
      return [
        caseItem.caseNumber || '',
        client?.fullName || '',
        ente,
        caseItem.subject || '',
        openingDate,
        scadenza,
        compenso,
        operatorName,
        caseItem.status || '',
        caseItem.priority || ''
      ];
    });
    
    const emptyRow = Array(numCols).fill("");
    const headerData = [
      ["Registro Pratiche", ...Array(numCols - 1).fill("")],
      ["Gruppo Impresa STP", ...Array(numCols - 1).fill("")],
      [`${gestioneLabel}  |  Periodo: dal 01/01/2026 al ${exportDateStr}  |  Esportato il: ${exportDateStr}`, ...Array(numCols - 1).fill("")],
      emptyRow,
      dataHeaders,
      ...dataRows
    ];
    
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(headerData);
    
    ws['!cols'] = [
      { wch: 14 }, { wch: 28 }, { wch: 22 }, { wch: 32 },
      { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 20 },
      { wch: 16 }, { wch: 10 },
    ];
    ws['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: numCols - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: numCols - 1 } },
      { s: { r: 2, c: 0 }, e: { r: 2, c: numCols - 1 } },
    ];
    
    applyRegisterStyle(ws, numCols, 4, headerData.length, [6], [0, 4, 5]);
    XLSX.utils.book_append_sheet(wb, ws, "Registro Pratiche");
    writeFileWithPageSetup(wb, `registro_pratiche_${fileDate()}.xlsx`, 72);
  }, [sortedFilteredCases, clients, operators, getOperatorName, filterPracticeType]);

  return (
    <TooltipProvider>
      <ModulePageLayout
        testid="pratiche-module"
        icon={FolderOpen}
        title="Registro Pratiche"
        subtitle={
          filterPracticeType && filterPracticeType !== "all"
            ? (filterPracticeType === "Gestionale Studio"
                ? "Gestione Studio"
                : "Gestione Agenzia")
            : undefined
        }
        primaryAction={
          onNewCase
            ? {
                label: "Nuova Pratica",
                onClick: onNewCase,
                icon: Plus,
                testid: "pratiche-new-case-btn",
              }
            : null
        }
        actions={
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrintPractices}
              className="h-9 text-xs px-3"
              data-testid="print-practices-button"
              disabled={sortedFilteredCases.length === 0}
              title={`Stampa ${sortedFilteredCases.length} righe filtrate`}
            >
              <Printer className="h-3.5 w-3.5 mr-1.5" /> Stampa
              <span className="ml-1 opacity-70">({sortedFilteredCases.length})</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportExcel}
              className="h-9 text-xs px-3"
              data-testid="export-excel-button"
              disabled={sortedFilteredCases.length === 0}
              title={`Esporta ${sortedFilteredCases.length} righe filtrate`}
            >
              <Download className="h-3.5 w-3.5 mr-1.5" /> Esporta
              <span className="ml-1 opacity-70">({sortedFilteredCases.length})</span>
            </Button>
          </>
        }
        kpiStrip={
          <>
            <KpiPill
              value={stats.nuoveRichieste}
              label="Nuove"
              tone="info"
              active={cardFilter === "Nuova richiesta"}
              onClick={() => handleCardClick("Nuova richiesta")}
              testid="filter-card-nuove-pratiche"
            />
            <KpiPill
              value={stats.inLavorazione}
              label="In Lavorazione"
              tone="warning"
              active={cardFilter === "In lavorazione"}
              onClick={() => handleCardClick("In lavorazione")}
              testid="filter-card-in-lavorazione"
            />
            <KpiPill
              value={stats.sospese}
              label="Sospese"
              tone="danger"
              active={cardFilter === "Sospesa"}
              onClick={() => handleCardClick("Sospesa")}
              testid="filter-card-sospese"
            />
            <KpiPill
              value={stats.completate}
              label="Concluse"
              tone="success"
              active={cardFilter === "Conclusa"}
              onClick={() => handleCardClick("Conclusa")}
              testid="filter-card-concluse"
            />
            {cardFilter && (
              <button
                onClick={() => { setCardFilter(null); setFilterStatus("all"); }}
                className="text-xs text-slate-400 hover:text-red-500 ml-1"
                title="Azzera filtro"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
            <div className="h-6 w-px bg-slate-200 mx-1" />
            {agenziaArchiveYears.length > 0 && (
              <div className="flex items-center gap-1.5" data-testid="agenzia-archive-nav">
                <span className="text-[10px] font-semibold text-orange-600 uppercase">AG</span>
                <Select
                  value={agenziaArchiveYear ? String(agenziaArchiveYear) : "all"}
                  onValueChange={(val) => {
                    if (val === "all") {
                      setAgenziaArchiveYear(null);
                      setFilterYear("all");
                      setFilterPracticeType("all");
                    } else {
                      handleArchiveYearClick(Number(val));
                    }
                  }}
                >
                  <SelectTrigger className="w-[120px] h-8 text-xs border-orange-200" data-testid="archive-year-dropdown">
                    <SelectValue placeholder="Anno" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutte</SelectItem>
                    {agenziaArchiveYears.map(y => (
                      <SelectItem key={y} value={String(y)}>{y} ({gaCountsByYear[y] || 0})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {studioArchiveYears.length > 0 && (
              <div className="flex items-center gap-1.5" data-testid="studio-archive-nav">
                <span className="text-[10px] font-semibold text-blue-600 uppercase">ST</span>
                <Select
                  value={studioArchiveYear ? String(studioArchiveYear) : "all"}
                  onValueChange={(val) => {
                    if (val === "all") {
                      setStudioArchiveYear(null);
                      setFilterYear("all");
                    } else {
                      handleStudioArchiveYearClick(Number(val));
                    }
                  }}
                >
                  <SelectTrigger className="w-[120px] h-8 text-xs border-blue-200" data-testid="studio-archive-year-dropdown">
                    <SelectValue placeholder="Anno" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutte</SelectItem>
                    {studioArchiveYears.map(y => (
                      <SelectItem key={y} value={String(y)}>{y} ({gsCountsByYear[y] || 0})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </>
        }
        toolbar={
          <>
            <div className="relative flex-1 min-w-[220px] max-w-[420px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                data-testid="search-cases-input"
                placeholder="Cerca pratica, cliente, oggetto..."
                value={searchInput}
                onChange={handleSearchInputChange}
                className="pl-8 h-9 text-sm"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[140px] h-9 text-xs" data-testid="filter-status-select">
                <SelectValue placeholder="Stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                {Object.keys(statusConfig).map((status) => (
                  <SelectItem key={status} value={status}>{status}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterPriority} onValueChange={setFilterPriority}>
              <SelectTrigger className="w-[130px] h-9 text-xs" data-testid="filter-priority-select">
                <SelectValue placeholder="Priorità" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte</SelectItem>
                {Object.keys(priorityConfig).map((priority) => (
                  <SelectItem key={priority} value={priority}>{priority}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterPracticeType || "all"} onValueChange={setFilterPracticeType}>
              <SelectTrigger
                className={cn(
                  "w-[160px] h-9 text-xs",
                  filterPracticeType === "Gestionale Studio" && "border-blue-400 bg-blue-50 text-blue-700 font-semibold",
                  filterPracticeType === "Gestionale Agenzia" && "border-orange-400 bg-orange-50 text-orange-700 font-semibold",
                )}
                data-testid="filter-practice-type-select"
              >
                <SelectValue placeholder="Tipo gestione" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i tipi</SelectItem>
                <SelectItem value="Gestionale Studio">Studio</SelectItem>
                <SelectItem value="Gestionale Agenzia">Agenzia</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterYear} onValueChange={setFilterYear}>
              <SelectTrigger className="w-[110px] h-9 text-xs" data-testid="filter-year-select">
                <Calendar className="h-3 w-3 mr-1" />
                <SelectValue placeholder="Anno" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli anni</SelectItem>
                {availableYears.map(y => (
                  <SelectItem key={y} value={y}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="ml-auto flex items-center gap-2 text-xs text-slate-500 pr-1">
              <Filter className="h-3 w-3" />
              {sortedFilteredCases.length === cases.length
                ? `${cases.length} pratiche`
                : `${sortedFilteredCases.length} di ${cases.length}`}
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={resetFilters}
                  className="h-7 text-xs text-slate-500 hover:text-slate-700 px-2"
                  data-testid="reset-filters"
                >
                  <X className="h-3 w-3 mr-1" /> Reset
                </Button>
              )}
            </div>
          </>
        }
      >
        {/* ── AREA OPERATIVA: Tabella ─────────────────────── */}
        <Card className="shadow-sm">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="w-[100px] text-xs font-semibold">N. Pratica</TableHead>
                <TableHead className="text-xs font-semibold">Cliente / Oggetto</TableHead>
                <TableHead className="w-[90px] text-xs font-semibold hidden md:table-cell">Data</TableHead>
                <TableHead className="w-[100px] text-xs font-semibold hidden lg:table-cell">Compenso</TableHead>
                <TableHead className="w-[100px] text-xs font-semibold">Stato</TableHead>
                <TableHead className="w-[70px] text-xs font-semibold text-center">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pagedCases.map((caseItem) => (
                <PracticeRow
                  key={caseItem.id}
                  caseItem={caseItem}
                  client={clientsById.get(caseItem.clientId)}
                  isExpanded={expandedRow === caseItem.id}
                  isShowingNotes={showNotesForCase === caseItem.id}
                  isPrintingMandato={printingCaseId === caseItem.id}
                  isPrintingDossier={printingDossierId === caseItem.id}
                  isSavingNote={savingNoteFor === caseItem.id}
                  quickNoteValue={quickNoteInput[caseItem.id] || ""}
                  allowPrint={canPrintMandato(caseItem)}
                  currentOperator={currentOperator}
                  onToggleExpand={toggleRowExpansion}
                  onToggleNotes={toggleNotes}
                  onEdit={handleEditCase}
                  onStatusChange={handleQuickStatusChange}
                  onPrintMandato={handleQuickPrintMandato}
                  onPrintDossier={handleQuickPrintDossier}
                  onQuickNoteSubmit={handleQuickNoteSubmit}
                  onQuickNoteChange={handleQuickNoteChange}
                  getOperatorName={getOperatorName}
                  getDisplayNotes={getDisplayNotes}
                />
              ))}

              {/* Empty State */}
              {sortedFilteredCases.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="py-12 text-center">
                    <Briefcase className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <h3 className="text-sm font-medium text-slate-700 mb-1">
                      Nessuna pratica trovata
                    </h3>
                    <p className="text-xs text-slate-500">
                      {hasActiveFilters
                        ? "Prova a modificare i filtri"
                        : "Clicca su 'Nuova Pratica' per iniziare"}
                    </p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {casesPaginationBar}
        </Card>
      </ModulePageLayout>
    </TooltipProvider>
  );
}

export default React.memo(PraticheTab);

